/**
 * Trip sharing v1 — shared building blocks of the VISITOR surface
 * (/s/<token> pages): the read-only strip, the password gate, the terminal
 * states (expired / not found), the action-wall dialog, and small hooks.
 *
 * The wall implements the product decision (Paulo 2026-09-10): every edit
 * affordance stays VISIBLE on the shared view; tapping one opens this dialog.
 *  - Signed out → log in / sign up (with ?next= back to this page).
 *  - Signed in (non-owner) → "editing shared trips isn't ready yet": the tap
 *    itself registers the V2 desirability signal (edit-request endpoint, once
 *    per trip per browser session) and the dialog offers "Save a copy" as the
 *    path that works today.
 *
 * Pages: pages/SharedTripPage.tsx, pages/SharedJourneyPage.tsx.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/button';
import useViewportWidth from '../../hooks/useViewportWidth';
import { useAuth } from '../auth/context';
import { hasGuestSession } from '../auth/guest';
import { fetchOwnerPath, requestEditAccess } from './api';
import ShareButton from './ShareButton';

/** Robots noindex for capability-URL pages (they must never be indexed). */
export function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);
}

/**
 * The owner opening their own share link lands on their editable page
 * (/trips/<id> or /journeys/<id>) instead of the read-only view (Paulo
 * 2026-10-03). Returns false while that is being checked: the page shows its
 * loader and fetches nothing, so the owner never meets their own password
 * gate and never counts as a share-link view. Anyone else (and anyone
 * without a session) gets `true` and the shared view.
 */
export function useOwnerRedirect(token: string | undefined, journeyId?: string): boolean {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = user?.id ?? null;
  const [checked, setChecked] = useState(false);
  // Read once per check, not a dependency: carrying ?focus= etc. over must
  // not re-run the check.
  const searchRef = useRef(location.search);
  searchRef.current = location.search;

  useEffect(() => {
    if (loading) return;
    if (!token || (!userId && !hasGuestSession())) { setChecked(true); return; }
    let alive = true;
    fetchOwnerPath(token, journeyId).then((path) => {
      if (!alive) return;
      if (path) navigate(path + searchRef.current, { replace: true });
      else setChecked(true);
    });
    return () => { alive = false; };
  }, [loading, userId, token, journeyId, navigate]);

  return checked;
}

// ─────────────────────────────────────────────────────────────────────────────
// Nav actions — the visitor's two funnel actions, as VISIBLE buttons
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The shared views' header pair (UX decision, Paulo 2026-09-14): a visitor
 * has exactly two real actions — reshare and Save a copy — and funnel actions
 * live in visible buttons, not an overflow menu. One responsive pattern: on
 * narrow screens Share collapses to an icon-only button.
 */
export const SharedNavActions: React.FC<{
  onShare: () => void;
  onSaveCopy: () => void;
  savingCopy?: boolean;
}> = ({ onShare, onSaveCopy, savingCopy }) => {
  const compact = useViewportWidth() < 430;
  return (
    <>
      <ShareButton onClick={onShare} compact={compact} />
      <button
        type="button"
        onClick={onSaveCopy}
        disabled={savingCopy}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: '7px 14px', borderRadius: 999, border: 'none',
          background: '#157a55', color: '#ffffff', fontSize: 13, fontWeight: 700,
          cursor: savingCopy ? 'default' : 'pointer', opacity: savingCopy ? 0.6 : 1,
          flexShrink: 0, whiteSpace: 'nowrap',
        }}
      >
        {savingCopy ? 'Saving…' : 'Save a copy'}
      </button>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Read-only strip
// ─────────────────────────────────────────────────────────────────────────────

export const SharedTripStrip: React.FC<{ ownerFirstName: string | null }> = ({ ownerFirstName }) => (
  <div
    style={{
      background: '#e8f2ec',
      border: '1px solid #cfe3d8',
      borderRadius: 12,
      padding: '10px 14px',
      marginBottom: 14,
      fontSize: 13,
      color: '#16241d',
      lineHeight: 1.5,
    }}
  >
    {ownerFirstName
      ? <>You're viewing <strong>{ownerFirstName}'s</strong> shared trip. It's read-only for now.</>
      : <>You're viewing a shared trip. It's read-only for now.</>}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Terminal states (card layout shared with the calculator's quota cards)
// ─────────────────────────────────────────────────────────────────────────────

const StateCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
    <div style={{ maxWidth: 440, margin: '0 auto', padding: '64px 16px' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '28px 24px', textAlign: 'center', boxShadow: '0 1px 3px rgba(20,40,30,0.10)' }}>
        <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 22, fontWeight: 600, color: '#16241d', margin: '0 0 10px' }}>{title}</h1>
        <div style={{ fontSize: 14, color: '#6c7872', lineHeight: 1.6 }}>{children}</div>
      </div>
    </div>
  </div>
);

export const ShareNotFound: React.FC = () => (
  <StateCard title="This link is not available">
    The trip may have been unshared or the link mistyped. Ask the person who
    sent it for a fresh link, or <a href="/" style={{ color: '#157a55', fontWeight: 600 }}>plan your own trip with Jody</a>.
  </StateCard>
);

export const ShareExpired: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  return (
    <StateCard title="This trip has expired">
      <p style={{ margin: '0 0 16px' }}>
        The trip behind this link was planned without an account and has expired.
        Its creator can restore it by signing up on the device it was made on.
      </p>
      <Button type="button" onClick={() => navigate(`/signup?next=${next}`)}>Create a free account</Button>
      <p style={{ margin: '12px 0 0', fontSize: 13 }}>
        Already have one? <button type="button" onClick={() => navigate(`/login?next=${next}`)} style={{ background: 'none', border: 0, padding: 0, color: '#157a55', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Log in</button>
      </p>
    </StateCard>
  );
};

export const ShareTooManyAttempts: React.FC = () => (
  <StateCard title="Too many tries">
    Please wait a while before trying the password again.
  </StateCard>
);

/** Retryable failure (no HTTP response): never a dead end. */
export const ShareLoadError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <StateCard title="Could not load this trip">
    <p style={{ margin: '0 0 16px' }}>
      Something interrupted the connection. Check your internet and try again.
    </p>
    <Button type="button" onClick={onRetry}>Try again</Button>
  </StateCard>
);

// ─────────────────────────────────────────────────────────────────────────────
// Password gate
// ─────────────────────────────────────────────────────────────────────────────

export const SharePasswordGate: React.FC<{
  error: boolean;
  busy: boolean;
  onSubmit: (password: string) => void;
}> = ({ error, busy, onSubmit }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim()) onSubmit(value.trim());
  };

  return (
    <StateCard title="This trip is protected">
      <p style={{ margin: '0 0 14px' }}>
        Enter the password from the message you received to open it.
      </p>
      <form onSubmit={submit}>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Password"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-label="Share password"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 15,
            textAlign: 'center', letterSpacing: 1,
            border: error ? '1.5px solid #cf5a3c' : '1px solid rgba(20,40,30,0.18)',
            borderRadius: 10, marginBottom: 8, fontFamily: 'inherit',
          }}
        />
        {error && (
          <div style={{ fontSize: 12.5, color: '#cf5a3c', marginBottom: 8 }}>
            That password is not right. Check the message you received.
          </div>
        )}
        <Button type="submit" disabled={busy || !value.trim()} style={{ width: '100%' }}>
          {busy ? 'Opening…' : 'Open the trip'}
        </Button>
      </form>
    </StateCard>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Action wall
// ─────────────────────────────────────────────────────────────────────────────

const editRequestKey = (token: string) => `jodyShareEditReq:${token}`;

export interface ShareWallDialogProps {
  token: string;
  /** True when a real account (JWT) is signed in. Guests count as signed out. */
  signedIn: boolean;
  ownerFirstName: string | null;
  savingCopy: boolean;
  onSaveCopy: () => void;
  onClose: () => void;
}

export const ShareWallDialog: React.FC<ShareWallDialogProps> = ({ token, signedIn, ownerFirstName, savingCopy, onSaveCopy, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { primaryRef.current?.focus(); }, []);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Signed-in tap = the desirability signal. Registered once per trip per
  // browser session so repeated taps don't spam the admin alert email.
  useEffect(() => {
    if (!signedIn) return;
    try {
      if (sessionStorage.getItem(editRequestKey(token))) return;
      sessionStorage.setItem(editRequestKey(token), '1');
    } catch { /* still fire the request */ }
    requestEditAccess(token).catch(() => { /* best-effort signal */ });
  }, [signedIn, token]);

  const next = encodeURIComponent(location.pathname + location.search);
  const owner = ownerFirstName || 'the trip owner';

  const body = signedIn ? (
    <>
      <h3 style={{ fontSize: 17, fontWeight: 700, color: '#16241d', margin: '0 0 8px' }}>
        Editing shared trips isn't ready yet
      </h3>
      <p style={{ fontSize: 14, color: '#6c7872', lineHeight: 1.6, margin: '0 0 16px' }}>
        This trip belongs to {owner}, and shared trips are read-only for now.
        We noted your interest, it helps us decide what to build next.
        Want to change this route today? Save a copy to your own trips.
      </p>
      <Button ref={primaryRef} type="button" onClick={onSaveCopy} disabled={savingCopy} style={{ width: '100%' }}>
        {savingCopy ? 'Saving…' : 'Save a copy to my trips'}
      </Button>
      <button
        type="button"
        onClick={onClose}
        style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 0, color: '#6c7872', fontSize: 13, cursor: 'pointer' }}
      >
        Keep viewing
      </button>
    </>
  ) : (
    <>
      <h3 style={{ fontSize: 17, fontWeight: 700, color: '#16241d', margin: '0 0 8px' }}>
        This is {owner}'s trip
      </h3>
      <p style={{ fontSize: 14, color: '#6c7872', lineHeight: 1.6, margin: '0 0 16px' }}>
        You can view everything right here. To save a copy to your own trips or
        plan journeys of your own, create a free Jody account.
      </p>
      <Button ref={primaryRef} type="button" onClick={() => navigate(`/signup?next=${next}`)} style={{ width: '100%' }}>
        Create a free account
      </Button>
      <p style={{ margin: '12px 0 0', textAlign: 'center', fontSize: 13, color: '#6c7872' }}>
        Already have one?{' '}
        <button
          type="button"
          onClick={() => navigate(`/login?next=${next}`)}
          style={{ background: 'none', border: 0, padding: 0, color: '#157a55', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
        >
          Log in
        </button>
      </p>
    </>
  );

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(15, 26, 20, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: 'min(400px, 94vw)', background: '#fff', borderRadius: 16,
          padding: '22px 22px 18px', boxShadow: '0 24px 56px rgba(15, 23, 42, 0.25)', position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ position: 'absolute', top: 12, right: 12, padding: '4px 8px', fontSize: 16, lineHeight: 1, background: 'transparent', border: 0, color: '#6c7872', cursor: 'pointer' }}
        >
          ×
        </button>
        {body}
      </div>
    </div>,
    document.body,
  );
};
